/**
 * Minimal, dependency-free QR Code encoder (byte mode, versions 1–40).
 *
 * baba needs exactly one QR: an account-link URL of roughly 45 characters.
 * That fits comfortably inside version 4 at error-correction level M, so
 * rather than pull a general-purpose npm encoder (and its lockfile churn)
 * into the web bundle, this module implements the slice of ISO/IEC 18004
 * that slice of the problem actually needs:
 *
 *   - byte mode only (the URL contains lowercase letters, so alphanumeric
 *     mode is unavailable anyway)
 *   - versions 1–40, which hold website page and entry context alongside the
 *     opaque click ID without truncating attribution
 *   - all four error-correction levels, all eight data masks
 *
 * Everything else (kanji mode, ECI, structured append, versions beyond 40) is
 * deliberately absent. `encodeQr` throws rather than silently truncating if
 * the payload does not fit.
 */

export type EcLevel = 'L' | 'M' | 'Q' | 'H'

export interface QrMatrix {
  /** Modules per side, excluding the quiet zone. */
  readonly size: number
  /** Chosen symbol version, 1–40. */
  readonly version: number
  /** Error-correction level the symbol was built with. */
  readonly ecLevel: EcLevel
  /** Chosen data mask, 0–7. */
  readonly mask: number
  /** `modules[row][col]` — true means a dark module. */
  readonly modules: readonly (readonly boolean[])[]
}

/** Total codewords (data + error correction) per version, index 1–40. */
const TOTAL_CODEWORDS = [0, 26, 44, 70, 100, 134, 172, 196, 242, 292, 346, 404, 466, 532, 581, 655, 733, 815, 901, 991, 1085, 1156, 1258, 1364, 1474, 1588, 1706, 1828, 1921, 2051, 2185, 2323, 2465, 2611, 2761, 2876, 3034, 3196, 3362, 3532, 3706]

/**
 * Per version and EC level: [ecCodewordsPerBlock, blocksInGroup1,
 * dataCodewordsPerGroup1Block, blocksInGroup2, dataCodewordsPerGroup2Block].
 * Group-2 blocks hold exactly one more data codeword than group-1 blocks.
 * Transcribed from ISO/IEC 18004 Table 9; every row is asserted against
 * TOTAL_CODEWORDS by the test suite.
 */
const EC_BLOCKS: Record<EcLevel, ReadonlyArray<readonly [number, number, number, number, number]>> = {
  L: [
    [0, 0, 0, 0, 0],
    [7, 1, 19, 0, 0],
    [10, 1, 34, 0, 0],
    [15, 1, 55, 0, 0],
    [20, 1, 80, 0, 0],
    [26, 1, 108, 0, 0],
    [18, 2, 68, 0, 0],
    [20, 2, 78, 0, 0],
    [24, 2, 97, 0, 0],
    [30, 2, 116, 0, 0],
    [18, 2, 68, 2, 69],
    [20, 4, 81, 0, 0],
    [24, 2, 92, 2, 93],
    [26, 4, 107, 0, 0],
    [30, 3, 115, 1, 116],
    [22, 5, 87, 1, 88],
    [24, 5, 98, 1, 99],
    [28, 1, 107, 5, 108],
    [30, 5, 120, 1, 121],
    [28, 3, 113, 4, 114],
    [28, 3, 107, 5, 108],
    [28, 4, 116, 4, 117],
    [28, 2, 111, 7, 112],
    [30, 4, 121, 5, 122],
    [30, 6, 117, 4, 118],
    [26, 8, 106, 4, 107],
    [28, 10, 114, 2, 115],
    [30, 8, 122, 4, 123],
    [30, 3, 117, 10, 118],
    [30, 7, 116, 7, 117],
    [30, 5, 115, 10, 116],
    [30, 13, 115, 3, 116],
    [30, 17, 115, 0, 0],
    [30, 17, 115, 1, 116],
    [30, 13, 115, 6, 116],
    [30, 12, 121, 7, 122],
    [30, 6, 121, 14, 122],
    [30, 17, 122, 4, 123],
    [30, 4, 122, 18, 123],
    [30, 20, 117, 4, 118],
    [30, 19, 118, 6, 119],
  ],
  M: [
    [0, 0, 0, 0, 0],
    [10, 1, 16, 0, 0],
    [16, 1, 28, 0, 0],
    [26, 1, 44, 0, 0],
    [18, 2, 32, 0, 0],
    [24, 2, 43, 0, 0],
    [16, 4, 27, 0, 0],
    [18, 4, 31, 0, 0],
    [22, 2, 38, 2, 39],
    [22, 3, 36, 2, 37],
    [26, 4, 43, 1, 44],
    [30, 1, 50, 4, 51],
    [22, 6, 36, 2, 37],
    [22, 8, 37, 1, 38],
    [24, 4, 40, 5, 41],
    [24, 5, 41, 5, 42],
    [28, 7, 45, 3, 46],
    [28, 10, 46, 1, 47],
    [26, 9, 43, 4, 44],
    [26, 3, 44, 11, 45],
    [26, 3, 41, 13, 42],
    [26, 17, 42, 0, 0],
    [28, 17, 46, 0, 0],
    [28, 4, 47, 14, 48],
    [28, 6, 45, 14, 46],
    [28, 8, 47, 13, 48],
    [28, 19, 46, 4, 47],
    [28, 22, 45, 3, 46],
    [28, 3, 45, 23, 46],
    [28, 21, 45, 7, 46],
    [28, 19, 47, 10, 48],
    [28, 2, 46, 29, 47],
    [28, 10, 46, 23, 47],
    [28, 14, 46, 21, 47],
    [28, 14, 46, 23, 47],
    [28, 12, 47, 26, 48],
    [28, 6, 47, 34, 48],
    [28, 29, 46, 14, 47],
    [28, 13, 46, 32, 47],
    [28, 40, 47, 7, 48],
    [28, 18, 47, 31, 48],
  ],
  Q: [
    [0, 0, 0, 0, 0],
    [13, 1, 13, 0, 0],
    [22, 1, 22, 0, 0],
    [18, 2, 17, 0, 0],
    [26, 2, 24, 0, 0],
    [18, 2, 15, 2, 16],
    [24, 4, 19, 0, 0],
    [18, 2, 14, 4, 15],
    [22, 4, 18, 2, 19],
    [20, 4, 16, 4, 17],
    [24, 6, 19, 2, 20],
    [28, 4, 22, 4, 23],
    [26, 4, 20, 6, 21],
    [24, 8, 20, 4, 21],
    [20, 11, 16, 5, 17],
    [30, 5, 24, 7, 25],
    [24, 15, 19, 2, 20],
    [28, 1, 22, 15, 23],
    [28, 17, 22, 1, 23],
    [26, 17, 21, 4, 22],
    [30, 15, 24, 5, 25],
    [28, 17, 22, 6, 23],
    [30, 7, 24, 16, 25],
    [30, 11, 24, 14, 25],
    [30, 11, 24, 16, 25],
    [30, 7, 24, 22, 25],
    [28, 28, 22, 6, 23],
    [30, 8, 23, 26, 24],
    [30, 4, 24, 31, 25],
    [30, 1, 23, 37, 24],
    [30, 15, 24, 25, 25],
    [30, 42, 24, 1, 25],
    [30, 10, 24, 35, 25],
    [30, 29, 24, 19, 25],
    [30, 44, 24, 7, 25],
    [30, 39, 24, 14, 25],
    [30, 46, 24, 10, 25],
    [30, 49, 24, 10, 25],
    [30, 48, 24, 14, 25],
    [30, 43, 24, 22, 25],
    [30, 34, 24, 34, 25],
  ],
  H: [
    [0, 0, 0, 0, 0],
    [17, 1, 9, 0, 0],
    [28, 1, 16, 0, 0],
    [22, 2, 13, 0, 0],
    [16, 4, 9, 0, 0],
    [22, 2, 11, 2, 12],
    [28, 4, 15, 0, 0],
    [26, 4, 13, 1, 14],
    [26, 4, 14, 2, 15],
    [24, 4, 12, 4, 13],
    [28, 6, 15, 2, 16],
    [24, 3, 12, 8, 13],
    [28, 7, 14, 4, 15],
    [22, 12, 11, 4, 12],
    [24, 11, 12, 5, 13],
    [24, 11, 12, 7, 13],
    [30, 3, 15, 13, 16],
    [28, 2, 14, 17, 15],
    [28, 2, 14, 19, 15],
    [26, 9, 13, 16, 14],
    [28, 15, 15, 10, 16],
    [30, 19, 16, 6, 17],
    [24, 34, 13, 0, 0],
    [30, 16, 15, 14, 16],
    [30, 30, 16, 2, 17],
    [30, 22, 15, 13, 16],
    [30, 33, 16, 4, 17],
    [30, 12, 15, 28, 16],
    [30, 11, 15, 31, 16],
    [30, 19, 15, 26, 16],
    [30, 23, 15, 25, 16],
    [30, 23, 15, 28, 16],
    [30, 19, 15, 35, 16],
    [30, 11, 15, 46, 16],
    [30, 59, 16, 1, 17],
    [30, 22, 15, 41, 16],
    [30, 2, 15, 64, 16],
    [30, 24, 15, 46, 16],
    [30, 42, 15, 32, 16],
    [30, 10, 15, 67, 16],
    [30, 20, 15, 61, 16],
  ],
}

/** Alignment-pattern centre coordinates per version (ISO/IEC 18004 Annex E). */
const ALIGNMENT_POSITIONS: ReadonlyArray<readonly number[]> = [[], [], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50], [6, 30, 54], [6, 32, 58], [6, 34, 62], [6, 26, 46, 66], [6, 26, 48, 70], [6, 26, 50, 74], [6, 30, 54, 78], [6, 30, 56, 82], [6, 30, 58, 86], [6, 34, 62, 90], [6, 28, 50, 72, 94], [6, 26, 50, 74, 98], [6, 30, 54, 78, 102], [6, 28, 54, 80, 106], [6, 32, 58, 84, 110], [6, 30, 58, 86, 114], [6, 34, 62, 90, 118], [6, 26, 50, 74, 98, 122], [6, 30, 54, 78, 102, 126], [6, 26, 52, 78, 104, 130], [6, 30, 56, 82, 108, 134], [6, 34, 60, 86, 112, 138], [6, 30, 58, 86, 114, 142], [6, 34, 62, 90, 118, 146], [6, 30, 54, 78, 102, 126, 150], [6, 24, 50, 76, 102, 128, 154], [6, 28, 54, 80, 106, 132, 158], [6, 32, 58, 84, 110, 136, 162], [6, 26, 54, 82, 110, 138, 166], [6, 30, 58, 86, 114, 142, 170]]

export const MAX_VERSION = 40

// ─────────────────────────── GF(256) arithmetic ───────────────────────────

const GF_EXP = new Uint8Array(512)
const GF_LOG = new Uint8Array(256)

{
  let x = 1
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x
    GF_LOG[x] = i
    x <<= 1
    // QR's primitive polynomial: x^8 + x^4 + x^3 + x^2 + 1.
    if (x & 0x100) x ^= 0x11d
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255]
}

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0
  return GF_EXP[GF_LOG[a] + GF_LOG[b]]
}

/** Reed-Solomon generator polynomial of the given degree, high term first. */
function generatorPoly(degree: number): Uint8Array {
  let poly = new Uint8Array([1])
  for (let i = 0; i < degree; i++) {
    const next = new Uint8Array(poly.length + 1)
    for (let j = 0; j < poly.length; j++) {
      next[j] ^= poly[j]
      next[j + 1] ^= gfMul(poly[j], GF_EXP[i])
    }
    poly = next
  }
  return poly
}

/** The `ecLen` Reed-Solomon check codewords for one data block. */
export function reedSolomonEncode(data: Uint8Array, ecLen: number): Uint8Array {
  const gen = generatorPoly(ecLen)
  const remainder = new Uint8Array(ecLen)
  for (const byte of data) {
    const factor = byte ^ remainder[0]
    remainder.copyWithin(0, 1)
    remainder[ecLen - 1] = 0
    for (let i = 0; i < ecLen; i++) remainder[i] ^= gfMul(gen[i + 1], factor)
  }
  return remainder
}

// ───────────────────────────── bit assembly ─────────────────────────────

class BitBuffer {
  readonly bits: number[] = []

  put(value: number, length: number): void {
    for (let i = length - 1; i >= 0; i--) this.bits.push((value >>> i) & 1)
  }

  get length(): number {
    return this.bits.length
  }
}

/** UTF-8 bytes for `text`, without depending on TextEncoder's availability. */
export function utf8Bytes(text: string): Uint8Array {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(text)
  const out: number[] = []
  for (const char of text) {
    const cp = char.codePointAt(0) as number
    if (cp < 0x80) out.push(cp)
    else if (cp < 0x800) out.push(0xc0 | (cp >> 6), 0x80 | (cp & 0x3f))
    else if (cp < 0x10000) out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f))
    else out.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 0x3f), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f))
  }
  return Uint8Array.from(out)
}

/** Data codewords available (before EC) for a version + level. */
export function dataCodewordCount(version: number, ecLevel: EcLevel): number {
  const spec = EC_BLOCKS[ecLevel][version]
  return spec[1] * spec[2] + spec[3] * spec[4]
}

/** Total codewords (data + EC) for a version — exported for spec assertions. */
export function totalCodewordCount(version: number): number {
  return TOTAL_CODEWORDS[version]
}

/** The raw [ecPerBlock, blocks1, data1, blocks2, data2] row — for assertions. */
export function ecBlockSpec(version: number, ecLevel: EcLevel): readonly number[] {
  return EC_BLOCKS[ecLevel][version]
}

/** Byte-mode character-count indicator width. 16 bits from version 10 up. */
function charCountBits(version: number): number {
  return version < 10 ? 8 : 16
}

/** Smallest version 1–40 that holds `byteLength` bytes at `ecLevel`. */
export function chooseVersion(byteLength: number, ecLevel: EcLevel, minVersion = 1): number {
  for (let version = Math.max(1, minVersion); version <= MAX_VERSION; version++) {
    const required = 4 + charCountBits(version) + byteLength * 8
    if (required <= dataCodewordCount(version, ecLevel) * 8) return version
  }
  throw new Error(
    `QR payload of ${byteLength} bytes does not fit in version ${MAX_VERSION} at level ${ecLevel}`,
  )
}

/** Mode indicator, length, payload, terminator, and the 0xEC/0x11 pad tail. */
function buildDataCodewords(bytes: Uint8Array, version: number, ecLevel: EcLevel): Uint8Array {
  const capacity = dataCodewordCount(version, ecLevel)
  const buffer = new BitBuffer()
  buffer.put(0b0100, 4) // byte mode
  buffer.put(bytes.length, charCountBits(version))
  for (const byte of bytes) buffer.put(byte, 8)

  const capacityBits = capacity * 8
  buffer.put(0, Math.min(4, capacityBits - buffer.length))
  while (buffer.length % 8 !== 0) buffer.put(0, 1)

  const codewords = new Uint8Array(capacity)
  for (let i = 0; i < buffer.length; i += 8) {
    let byte = 0
    for (let b = 0; b < 8; b++) byte = (byte << 1) | buffer.bits[i + b]
    codewords[i / 8] = byte
  }
  for (let i = buffer.length / 8, alt = 0; i < capacity; i++, alt++) {
    codewords[i] = alt % 2 === 0 ? 0xec : 0x11
  }
  return codewords
}

/** Split into EC blocks, compute check codewords, and interleave both halves. */
function interleaveBlocks(data: Uint8Array, version: number, ecLevel: EcLevel): Uint8Array {
  const [ecPerBlock, g1, d1, g2, d2] = EC_BLOCKS[ecLevel][version]
  const dataBlocks: Uint8Array[] = []
  const ecBlocks: Uint8Array[] = []

  let offset = 0
  for (let i = 0; i < g1 + g2; i++) {
    const length = i < g1 ? d1 : d2
    const block = data.subarray(offset, offset + length)
    offset += length
    dataBlocks.push(block)
    ecBlocks.push(reedSolomonEncode(block, ecPerBlock))
  }

  const result: number[] = []
  const maxData = Math.max(d1, d2)
  for (let i = 0; i < maxData; i++) {
    for (const block of dataBlocks) if (i < block.length) result.push(block[i])
  }
  for (let i = 0; i < ecPerBlock; i++) {
    for (const block of ecBlocks) result.push(block[i])
  }
  return Uint8Array.from(result)
}

// ────────────────────────── matrix construction ──────────────────────────

/** BCH(15,5)-protected format information, XOR-masked per the spec. */
export function formatInfoBits(ecLevel: EcLevel, mask: number): number {
  const levelBits: Record<EcLevel, number> = { L: 0b01, M: 0b00, Q: 0b11, H: 0b10 }
  const data = (levelBits[ecLevel] << 3) | mask
  let remainder = data
  for (let i = 0; i < 10; i++) remainder = (remainder << 1) ^ (((remainder >>> 9) & 1) * 0x537)
  return ((data << 10) | remainder) ^ 0x5412
}

/** BCH(18,6)-protected version information (versions 7 and up only). */
export function versionInfoBits(version: number): number {
  let remainder = version
  for (let i = 0; i < 12; i++) remainder = (remainder << 1) ^ (((remainder >>> 11) & 1) * 0x1f25)
  return (version << 12) | remainder
}

type Grid = boolean[][]

function maskAt(mask: number, row: number, col: number): boolean {
  switch (mask) {
    case 0: return (row + col) % 2 === 0
    case 1: return row % 2 === 0
    case 2: return col % 3 === 0
    case 3: return (row + col) % 3 === 0
    case 4: return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0
    case 5: return ((row * col) % 2) + ((row * col) % 3) === 0
    case 6: return (((row * col) % 2) + ((row * col) % 3)) % 2 === 0
    case 7: return (((row + col) % 2) + ((row * col) % 3)) % 2 === 0
    default: throw new Error(`Unknown QR mask ${mask}`)
  }
}

/** Finder, separator, timing, alignment, dark module, and reserved regions. */
function drawFunctionPatterns(modules: Grid, reserved: Grid, version: number): void {
  const size = modules.length

  const set = (row: number, col: number, dark: boolean) => {
    modules[row][col] = dark
    reserved[row][col] = true
  }

  // Three 7×7 finder patterns plus their one-module separators.
  const drawFinder = (top: number, left: number) => {
    for (let r = -1; r <= 7; r++) {
      for (let c = -1; c <= 7; c++) {
        const row = top + r
        const col = left + c
        if (row < 0 || row >= size || col < 0 || col >= size) continue
        const ring = Math.max(Math.abs(r - 3), Math.abs(c - 3))
        set(row, col, ring !== 2 && ring <= 3)
      }
    }
  }
  drawFinder(0, 0)
  drawFinder(0, size - 7)
  drawFinder(size - 7, 0)

  // Timing patterns along row 6 and column 6.
  for (let i = 8; i < size - 8; i++) {
    const dark = i % 2 === 0
    set(6, i, dark)
    set(i, 6, dark)
  }

  // 5×5 alignment patterns, minus the three that collide with finders.
  const positions = ALIGNMENT_POSITIONS[version]
  for (const centreRow of positions) {
    for (const centreCol of positions) {
      const nearTopLeft = centreRow === positions[0] && centreCol === positions[0]
      const nearTopRight = centreRow === positions[0] && centreCol === positions[positions.length - 1]
      const nearBottomLeft = centreRow === positions[positions.length - 1] && centreCol === positions[0]
      if (nearTopLeft || nearTopRight || nearBottomLeft) continue
      for (let r = -2; r <= 2; r++) {
        for (let c = -2; c <= 2; c++) {
          set(centreRow + r, centreCol + c, Math.max(Math.abs(r), Math.abs(c)) !== 1)
        }
      }
    }
  }

  // Format-information strips are reserved now and written after masking.
  for (let i = 0; i < 9; i++) {
    if (!reserved[8][i]) set(8, i, false)
    if (!reserved[i][8]) set(i, 8, false)
  }
  for (let i = 0; i < 8; i++) {
    if (!reserved[8][size - 1 - i]) set(8, size - 1 - i, false)
    if (!reserved[size - 1 - i][8]) set(size - 1 - i, 8, false)
  }
  // The always-dark module below the bottom-left finder.
  set(size - 8, 8, true)

  if (version >= 7) {
    const bits = versionInfoBits(version)
    for (let i = 0; i < 18; i++) {
      const dark = ((bits >> i) & 1) === 1
      const a = size - 11 + (i % 3)
      const b = Math.floor(i / 3)
      set(b, a, dark)
      set(a, b, dark)
    }
  }
}

/** Two-column right-to-left zigzag over every non-function module. */
function drawCodewords(modules: Grid, reserved: Grid, codewords: Uint8Array): void {
  const size = modules.length
  let bitIndex = 0
  for (let right = size - 1; right >= 1; right -= 2) {
    // Column 6 is the vertical timing pattern; the zigzag skips over it.
    if (right === 6) right = 5
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const col = right - j
        const upward = ((right + 1) & 2) === 0
        const row = upward ? size - 1 - vert : vert
        if (reserved[row][col]) continue
        if (bitIndex >= codewords.length * 8) continue
        const byte = codewords[bitIndex >>> 3]
        modules[row][col] = ((byte >>> (7 - (bitIndex & 7))) & 1) === 1
        bitIndex++
      }
    }
  }
}

function drawFormatInfo(modules: Grid, ecLevel: EcLevel, mask: number): void {
  const size = modules.length
  const bits = formatInfoBits(ecLevel, mask)
  for (let i = 0; i < 15; i++) {
    const dark = ((bits >> i) & 1) === 1
    // First copy, wrapping the top-left finder.
    if (i < 6) modules[i][8] = dark
    else if (i === 6) modules[7][8] = dark
    else if (i === 7) modules[8][8] = dark
    else if (i === 8) modules[8][7] = dark
    else modules[8][14 - i] = dark
    // Second copy, split between the other two finders.
    if (i < 8) modules[8][size - 1 - i] = dark
    else modules[size - 15 + i][8] = dark
  }
}

/** ISO/IEC 18004 §8.8.2 mask-evaluation penalty; lower is better. */
export function maskPenalty(modules: readonly (readonly boolean[])[]): number {
  const size = modules.length
  let penalty = 0

  // Rule 1 — runs of five or more same-coloured modules in a line.
  const scoreLine = (get: (i: number) => boolean) => {
    let runColour = get(0)
    let runLength = 1
    for (let i = 1; i < size; i++) {
      const value = get(i)
      if (value === runColour) {
        runLength++
      } else {
        if (runLength >= 5) penalty += 3 + (runLength - 5)
        runColour = value
        runLength = 1
      }
    }
    if (runLength >= 5) penalty += 3 + (runLength - 5)
  }
  for (let r = 0; r < size; r++) scoreLine((c) => modules[r][c])
  for (let c = 0; c < size; c++) scoreLine((r) => modules[r][c])

  // Rule 2 — every 2×2 block of one colour.
  for (let r = 0; r < size - 1; r++) {
    for (let c = 0; c < size - 1; c++) {
      const v = modules[r][c]
      if (v === modules[r][c + 1] && v === modules[r + 1][c] && v === modules[r + 1][c + 1]) penalty += 3
    }
  }

  // Rule 3 — the finder-like 1:1:3:1:1 ratio (dark:light:dark:dark:dark:
  // light:dark) preceded or followed by four light modules. A run touching
  // the symbol edge counts: the quiet zone supplies the light area.
  const FINDER = [true, false, true, true, true, false, true]
  const isLightRun = (get: (i: number) => boolean, from: number, to: number): boolean => {
    for (let i = Math.max(from, 0); i < Math.min(to, size); i++) if (get(i)) return false
    return true
  }
  const scanLine = (get: (i: number) => boolean) => {
    let i = 0
    while (i + 7 <= size) {
      let matched = true
      for (let k = 0; k < 7 && matched; k++) if (get(i + k) !== FINDER[k]) matched = false
      if (!matched) {
        i++
        continue
      }
      if (i === 0 || i === size - 7 || isLightRun(get, i - 4, i) || isLightRun(get, i + 7, i + 11)) {
        penalty += 40
        i += 7
      } else {
        // No light area either side: the earliest module that could start
        // another 1:1:3:1:1 run is the final dark module of this one.
        i += 4
      }
    }
  }
  for (let r = 0; r < size; r++) scanLine((c) => modules[r][c])
  for (let c = 0; c < size; c++) scanLine((r) => modules[r][c])

  // Rule 4 — deviation of the dark-module ratio from 50%.
  let dark = 0
  for (let r = 0; r < size; r++) for (let c = 0; c < size; c++) if (modules[r][c]) dark++
  const percent = (dark * 100) / (size * size)
  penalty += Math.floor(Math.abs(percent - 50) / 5) * 10

  return penalty
}

export interface EncodeQrOptions {
  /** Error-correction level. Defaults to 'M' — the usual print/screen choice. */
  ecLevel?: EcLevel
  /** Force at least this version, e.g. to keep a module size stable. */
  minVersion?: number
  /** Force a specific data mask instead of picking the lowest-penalty one. */
  mask?: number
}

/**
 * Encodes `text` as a QR symbol. Throws if the payload exceeds version 40 at
 * the requested error-correction level.
 */
export function encodeQr(text: string, options: EncodeQrOptions = {}): QrMatrix {
  const ecLevel = options.ecLevel ?? 'M'
  const bytes = utf8Bytes(text)
  const version = chooseVersion(bytes.length, ecLevel, options.minVersion)
  const codewords = interleaveBlocks(buildDataCodewords(bytes, version, ecLevel), version, ecLevel)

  const size = version * 4 + 17
  const base: Grid = Array.from({ length: size }, () => new Array<boolean>(size).fill(false))
  const reserved: Grid = Array.from({ length: size }, () => new Array<boolean>(size).fill(false))
  drawFunctionPatterns(base, reserved, version)
  drawCodewords(base, reserved, codewords)

  const candidates = options.mask !== undefined ? [options.mask] : [0, 1, 2, 3, 4, 5, 6, 7]
  let best: { mask: number; modules: Grid; penalty: number } | null = null
  for (const mask of candidates) {
    const modules = base.map((row) => row.slice())
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (!reserved[r][c] && maskAt(mask, r, c)) modules[r][c] = !modules[r][c]
      }
    }
    drawFormatInfo(modules, ecLevel, mask)
    const penalty = maskPenalty(modules)
    if (!best || penalty < best.penalty) best = { mask, modules, penalty }
  }
  if (!best) throw new Error('No QR mask candidate produced a symbol')

  return { size, version, ecLevel, mask: best.mask, modules: best.modules }
}

/**
 * An SVG path `d` covering every dark module, offset by `quietZone` modules.
 * One path for the whole symbol keeps the DOM to a single node.
 */
export function qrToSvgPath(matrix: QrMatrix, quietZone = 4): string {
  const parts: string[] = []
  for (let r = 0; r < matrix.size; r++) {
    for (let c = 0; c < matrix.size; c++) {
      if (matrix.modules[r][c]) parts.push(`M${c + quietZone} ${r + quietZone}h1v1h-1z`)
    }
  }
  return parts.join('')
}
